/* import __COLOCATED_TEMPLATE__ from './modal.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent-2 */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'ember-intl/services/intl';
import { inject as service } from '@ember/service';
import { type PlaygroundQuestionGeneration } from 'embercom/lib/fin-playground/question-generation';
import { TOPICS_EXPLORER_HELP_URL } from 'embercom/lib/fin-playground/topics';

interface Signature {
  Args: {
    isOpen: boolean;
    questionGeneration: PlaygroundQuestionGeneration | undefined;
    isLoading?: boolean;
    context?: 'new' | 'more';
    currentGroup?: { lastUsedTopicId?: string };
    onClose: () => void;
    onGenerate: (topicId?: string) => void;
  };
}

export default class TopicSelectionModal extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare intercomEventService: $TSFixMe;

  @tracked _selectedTopic: string | undefined = undefined;

  get selectedTopic() {
    // If we have a tracked selection, use it
    if (this._selectedTopic !== undefined) {
      return this._selectedTopic;
    }

    // Otherwise, use the default from lastUsedTopicId
    if (this.args.currentGroup?.lastUsedTopicId) {
      let availableTopics = this.args.questionGeneration?.availableTopics || [];
      return availableTopics.find(
        (topic) => topic.value === this.args.currentGroup?.lastUsedTopicId,
      )?.value;
    }

    return undefined;
  }

  set selectedTopic(value: string | undefined) {
    this._selectedTopic = value;
  }

  @action
  handleClose() {
    this.selectedTopic = undefined;
    this.args.onClose();
  }

  get learnMoreUrl() {
    return TOPICS_EXPLORER_HELP_URL;
  }

  get isGenerateDisabled() {
    return this.args.isLoading || !this.selectedTopic;
  }

  get modalTitle() {
    return this.args.context === 'more'
      ? this.intl.t('ai-agent.playground.modal.question-generation.heading-more')
      : this.intl.t('ai-agent.playground.modal.question-generation.heading');
  }

  get modalDescription() {
    return this.args.context === 'more'
      ? this.intl.t('ai-agent.playground.modal.question-generation.topics-description-more')
      : this.intl.t('ai-agent.playground.modal.question-generation.topics-description');
  }

  @action
  updateTopicSelection(topicId?: string) {
    this.selectedTopic = topicId;
  }

  @action
  trackLearnMoreClick() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'learn_more_insights_topics_link',
      context: 'playground',
      place: 'topic_selection_modal',
    });
  }

  @action
  handleGenerate() {
    this.args.onGenerate(this.selectedTopic);
    this.selectedTopic = undefined;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Playground::Modals::TopicSelection::Modal': typeof TopicSelectionModal;
    'ai-agent/playground/modals/topic-selection/modal': typeof TopicSelectionModal;
  }
}
